import {
    Directive,
    HostBinding,
    HostListener,
    Input,
    SimpleChanges
} from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { findRelativeActivatedRoute } from '../routing-helpers/routing-helpers';

/**
 * Route to a path relative to the current router URL, rather than the current component's active route.
 *
 * This directive works on `a` and `area` elements. The `href` attribute will be updated when the current URL changes.
 *
 * Should not be necessariy that much, but sometimes it is when navigation elements are in components
 * which are mounted up the navigation tree.
 *
 * Guidance from https://github.com/angular/angular/blob/14.3.0/packages/router/src/directives/router_link.ts
 */
@Directive({
    selector: 'a[relativeRouterLink],area[relativeRouterLink]'
})
export class RelativeRouterLinkWithHrefDirective {
    @Input()
    relativeRouterLink?: string;

    @Input() relativeRouteNodeType = 'dashboard';

    @HostBinding('attr.href')
    href: string;

    @HostBinding('attr.target') @Input() target!: string;

    private subscription: Subscription;

    constructor(private router: Router) {
        this.subscription = router.events.subscribe((s: Event) => {
            if (s instanceof NavigationEnd) {
                this.updateHref();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        this.updateHref();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    /** @nodoc */
    @HostListener('click', [
        '$event.button',
        '$event.ctrlKey',
        '$event.shiftKey',
        '$event.altKey',
        '$event.metaKey'
    ])
    onClick(
        button: number,
        ctrlKey: boolean,
        shiftKey: boolean,
        altKey: boolean,
        metaKey: boolean
    ): boolean {
        if (button !== 0 || ctrlKey || shiftKey || altKey || metaKey) {
            return true;
        }

        if (
            (typeof this.target === 'string' && this.target != '_self') ||
            this.relativeRouterLink == null
        ) {
            return true;
        }

        this.router.navigate([this.relativeRouterLink], {
            relativeTo: findRelativeActivatedRoute(
                this.router,
                this.relativeRouteNodeType
            ),
            queryParamsHandling: 'preserve'
        });
        return false;
    }

    private updateHref() {
        const urlTree = this.router.createUrlTree([this.relativeRouterLink], {
            relativeTo: findRelativeActivatedRoute(
                this.router,
                this.relativeRouteNodeType
            ),
            queryParamsHandling: 'preserve'
        });

        this.href = this.router.serializeUrl(urlTree);
    }
}
